import { Routes, Route, Navigate } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { AuthProvider } from '@/contexts/AuthContext';
import { Bounce, ToastContainer, toast } from 'react-toastify';
import 'react-toastify/ReactToastify.css';
import OrderPage from '@/pages/vendor/OrderPage';
import OrderDetails from '@/components/orders/OrderDetails';
import VendorAddress from '@/pages/vendor/VendorAddress';
import ProductPricing from '@/pages/vendor/ProductPricing';
import ProductPricingEdit from '@/pages/vendor/ProductPricingEdit';
import VendorRoute from '@/utils/ProtectedRoutes/VendorRoute';
import PublicLayoutRoute from '@/utils/ProtectedRoutes/PublicLayoutRoute';
import LoginPage from '@/pages/Auth/LoginPage';
import OTPPage from '@/pages/Auth/OTPPage';
import { UserProvider } from '@/contexts/UserContext';
import VendorProfile from '@/pages/vendor/vendorProfile/VendorProfile';
import VendorShopSettings from '@/pages/vendor/vendorProfile/VendorShopSettings';
import VendorProfileEdit from './pages/vendor/vendorProfile/ProfileEdit';
import ProductDetails from './pages/Store/ProductDetails';
import { OrderProvider } from './contexts/OrderContext';
import ProfileSocial from './pages/vendor/vendorProfile/ProfileSocial';
import CategoriesEdit from './pages/vendor/vendorProfile/CategoriesEdit';
import Contact from './pages/vendor/vendorProfile/Contact';
import VendorMedia from './pages/vendor/vendorProfile/VendorMedia';
import AboutBusiness from './pages/vendor/vendorProfile/AboutBusiness';
import ShopProfilePage from './pages/ShopProfilePage';
import ReturnPolicy from './pages/vendor/vendorProfile/ReturnPolicy';
import RefundPolicy from './pages/vendor/vendorProfile/RefundPolicy';
import Terms from './pages/vendor/vendorProfile/Terms';
import Privacy from './pages/vendor/vendorProfile/Privacy';
import ShippingPolicy from './pages/vendor/vendorProfile/ShippingPolicy';
import FAQ from './pages/vendor/vendorProfile/FAQ';
import LegalDisclaimer from './pages/vendor/vendorProfile/LegalDisclaimer';
import EventDashboard from './pages/EventDashboard';
import SettingsPage from './pages/vendor/SettingsPage';
import CouponManagementPage from './pages/vendor/CouponManagementPage';
import DeliverySlabManagement from './pages/vendor/DeliverySlabManagement';
import ChargeManagement from './pages/vendor/ChargeManagement';
import { useEffect } from 'react';
import { messaging, onMessage } from './config/firebase';
import { useNavigate } from 'react-router-dom';

function App() {
  const navigate = useNavigate();

  // Setup foreground notification handler
  useEffect(() => {
    if (!messaging) {
      return;
    }

    // Listen for foreground messages (when app is open)
    const unsubscribe = onMessage(messaging, (payload) => {
      console.log('📬 Foreground notification received:', payload);

      const { notification, data } = payload;
      const title = notification?.title || 'New Notification';
      const body = notification?.body || 'You have a new notification';
      const { type, orderId } = data || {};

      // Show toast notification with action
      toast.info(
        <div
          onClick={() => {
            // Navigate to order details if orderId is present
            if (type === 'ORDER_PLACED' || type === 'ORDER_STATUS_UPDATE') {
              if (orderId) {
                navigate(`/orders/${orderId}`);
              } else {
                navigate('/');
              }
            }
          }}
          className="cursor-pointer"
        >
          <div className="font-semibold">{title}</div>
          <div className="text-sm">{body}</div>
          <div className="text-xs text-gray-500 mt-1">Click to view</div>
        </div>,
        {
          autoClose: 5000,
          closeButton: true,
        }
      );
    });

    return () => {
      unsubscribe();
    };
  }, [navigate]);

  return (
    <HelmetProvider>
      <AuthProvider>
        <UserProvider>
          <OrderProvider>
            <div className="mx-auto">
              <Routes>
                {/* Public routes without layout */}
                <Route path="/login" element={<LoginPage />} />
                <Route path="/auth/otp" element={<OTPPage />} />

                {/* Public routes with VendorDashboardLayout */}
                <Route element={<PublicLayoutRoute />}>
                  <Route path="/return-policy" element={<ReturnPolicy />} />
                  <Route path="/refund-policy" element={<RefundPolicy />} />
                  <Route path="/shipping-policy" element={<ShippingPolicy />} />
                  <Route path="/terms" element={<Terms />} />
                  <Route path="/privacy" element={<Privacy />} />
                  <Route path="/faq" element={<FAQ />} />
                  <Route path="/legal-disclaimer" element={<LegalDisclaimer />} />
                </Route>

                {/* Protected vendor routes with VendorDashboardLayout */}
                <Route path="/" element={<VendorRoute />}>
                  <Route index element={<OrderPage />} />
                  <Route path="events" element={<EventDashboard />} />
                  <Route path="product/:id" element={<ProductDetails />} />
                  <Route path="/vendor/profile" element={<ShopProfilePage />} />
                  <Route path="profile" element={<VendorProfile />} />
                  <Route path="profile/edit" element={<VendorProfileEdit />} />
                  <Route path="/profile/business/edit" element={<AboutBusiness />} />
                  <Route path="profile/settings" element={<VendorShopSettings />} />
                  <Route path="profile/social" element={<ProfileSocial />} />
                  <Route path="profile/categories/edit" element={<CategoriesEdit />} />
                  <Route path="profile/media/edit" element={<VendorMedia />} />
                  <Route path="profile/contact" element={<Contact />} />
                  <Route path="address" element={<VendorAddress />} />
                  <Route path="product/pricing" element={<ProductPricing />} />
                  <Route path="product/pricing/:id" element={<ProductPricingEdit />} />
                  <Route path="orders/:id" element={<OrderDetails />} />
                  <Route path="settings" element={<SettingsPage />} />
                  <Route path="coupons" element={<CouponManagementPage />} />
                  <Route path="delivery-slabs" element={<DeliverySlabManagement />} />
                  <Route path="charges" element={<ChargeManagement />} />
                </Route>
                <Route path="*" element={<Navigate to="/" replace />} />
              </Routes>
              <ToastContainer
                position="top-center"
                autoClose={3000}
                hideProgressBar={false}
                newestOnTop={true}
                closeOnClick={true}
                rtl={false}
                draggable
                theme="light"
                transition={Bounce}
              />
            </div>
          </OrderProvider>
        </UserProvider>
      </AuthProvider>
    </HelmetProvider>
  );
}

export default App;
